import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTES } from '@traction/shared';
import { Mail, MessageSquare, FileText, CheckCircle } from 'lucide-react';

export function SupportPage() {
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    // In production, this would send to an API
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div className="py-12 bg-cacao-dark">
        <div className="mx-auto max-w-sm px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-500/20">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
          </div>
          <h1 className="mt-4 font-heading text-lg font-medium text-[#F5F5F5]">Message Sent</h1>
          <p className="mt-1.5 text-sm text-cacao-muted">
            Thanks for reaching out. We typically respond within 24 hours.
          </p>
          <Link
            to={ROUTES.HOME}
            className="mt-5 inline-block rounded-md bg-cacao-gold px-4 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 bg-cacao-dark">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h1 className="font-heading text-xl font-semibold text-[#F5F5F5]">Support</h1>
          <p className="mt-1.5 text-sm text-cacao-muted">
            We are here to help. Choose how you would like to reach us.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4 text-center">
            <Mail className="mx-auto h-4 w-4 text-cacao-gold" />
            <h3 className="mt-2.5 text-sm font-medium text-[#F5F5F5]">Email Us</h3>
            <p className="mt-1 text-xs text-cacao-muted">
              Send us an email anytime
            </p>
            <a
              href="mailto:support@tractionbee.com"
              className="mt-2.5 inline-block text-xs font-medium text-cacao-muted hover:text-white transition-colors"
            >
              support@tractionbee.com
            </a>
          </div>

          <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4 text-center">
            <MessageSquare className="mx-auto h-4 w-4 text-cacao-gold" />
            <h3 className="mt-2.5 text-sm font-medium text-[#F5F5F5]">FAQ</h3>
            <p className="mt-1 text-xs text-cacao-muted">
              Find answers to common questions
            </p>
            <Link
              to={ROUTES.FAQ}
              className="mt-2.5 inline-block text-xs font-medium text-cacao-muted hover:text-white transition-colors"
            >
              Browse FAQ
            </Link>
          </div>

          <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-4 text-center">
            <FileText className="mx-auto h-4 w-4 text-cacao-gold" />
            <h3 className="mt-2.5 text-sm font-medium text-[#F5F5F5]">Documentation</h3>
            <p className="mt-1 text-xs text-cacao-muted">
              Learn how to use Traction Bee
            </p>
            <span className="mt-2.5 inline-block text-xs text-cacao-text-tertiary">
              Coming soon
            </span>
          </div>
        </div>

        <div className="mt-8">
          <div className="rounded-lg border border-cacao-dark-tertiary bg-cacao-dark-secondary p-5">
            <h2 className="text-sm font-medium text-[#F5F5F5]">Send us a message</h2>
            <p className="mt-1 text-xs text-cacao-muted">
              Fill out the form below and we will get back to you within 24 hours.
            </p>

            <form onSubmit={handleSubmit} className="mt-4 space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-cacao-muted">
                    Name
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-cacao-muted">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-cacao-muted">
                  Subject
                </label>
                <input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-cacao-muted">
                  Message
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="mt-1 block w-full rounded-md border border-cacao-dark-tertiary bg-cacao-dark-tertiary px-3 py-2 text-sm text-[#F5F5F5] placeholder-cacao-text-tertiary focus:border-cacao-gold focus:outline-none focus:ring-1 focus:ring-cacao-gold"
                />
              </div>

              <button
                type="submit"
                className="w-full rounded-md bg-cacao-gold px-3 py-2 text-sm font-medium text-cacao-dark hover:bg-cacao-gold-hover transition-colors"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>

        <div className="mt-8 text-center text-xs text-cacao-text-tertiary">
          <p>
            <span className="font-medium text-cacao-muted">Traction Bee</span><br />
            26565 Agoura Road, Suite 200<br />
            Calabasas, CA 91302 USA
          </p>
        </div>
      </div>
    </div>
  );
}
